/**
 * Narrative-artifact cleanup shared by the web report (`walkCitationText`) and
 * the DOCX/PPTX exporters. Moved verbatim out of `walkCitationText.tsx` so the
 * exporters in the Cloud Run report-artifact bundle need no React renderer.
 */

// Confidence annotations the LLM (notably gemini-3.1-pro) leaks into prose as
// HTML comments — well-formed `<!-- confidence: high -->` OR the malformed
// `<!-- confidence: high ⟶` (the model emits the arrow as the comment close).
// The optional trailing arrow after a `-->` close is consumed too. Legit arrows
// in real content (e.g. "islanded → Battery") are NOT touched — only arrows that
// are part of a confidence comment are removed.
const CONFIDENCE_COMMENT_RE = /<!-{2,}\s*confidence:\s*[a-z_-]+\b[\s\S]*?(?:-{2,}!?>|⟶|—>)\s*(?:⟶|—>)?/gi;
const HTML_COMMENT_RE = /<!-{2,}[\s\S]*?(?:-{2,}!?>|⟶|—>)/g;

/**
 * Strip generation artifacts from narrative prose before rendering: leaked
 * confidence-annotation HTML comments and any other stray `<!-- … -->` comments
 * that were showing as literal text in report bodies. Idempotent + safe on
 * clean text.
 */
const DUPLICATE_RELIABILITY_REF_RE = /(\[(?:(?:verified|inferred|unverified)[^\]]*\b)?Ref\s+(\d+)\])(?:\s*,?\s*\[(?:(?:verified|inferred|unverified)[^\]]*\b)?Ref\s+\2\])+/gi;
const RAW_DOC_URI_RE = /\s*\(\s*doc:\/\/(?!sim\/)[^\s()]+\s*\)|,?\s*\bdoc:\/\/(?!sim\/)[^\s)\],;"']*/gi;

export function stripNarrativeArtifacts(text: string): string {
  if (!text) return text;
  const hasArtifact =
    text.includes('<!--') ||
    DUPLICATE_RELIABILITY_REF_RE.test(text) ||
    RAW_DOC_URI_RE.test(text);
  DUPLICATE_RELIABILITY_REF_RE.lastIndex = 0;
  RAW_DOC_URI_RE.lastIndex = 0;
  if (!hasArtifact) return text;
  return text
    .replace(CONFIDENCE_COMMENT_RE, '')
    .replace(HTML_COMMENT_RE, '')
    .replace(DUPLICATE_RELIABILITY_REF_RE, '$1')
    .replace(RAW_DOC_URI_RE, '')
    .replace(/\(\s*[,;:]\s*/g, '(')
    .replace(/\s*[,;:]\s*\)/g, ')')
    .replace(/\s*\(\s*\)/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/^[ \t]+/gm, '');
}


const INLINE_CONFIDENCE_OR_HTML_COMMENT_RE =
  /(?:<!-{2,}\s*confidence:\s*[a-z_-]+\b[\s\S]*?(?:-{2,}!?>|\u27f6|\u2014>)[ \t]*(?:(?:\u27f6|\u2014>)[ \t]*)?|<!-{2,}[\s\S]*?(?:-{2,}!?>|\u27f6|\u2014>)[ \t]*)/gi;

export function stripConfidenceComments(text: string): string {
  if (!text || !text.includes('<!--')) return text;
  return text.replace(INLINE_CONFIDENCE_OR_HTML_COMMENT_RE, '');
}

function isPlainRecord(value: object): boolean {
  const proto = Object.getPrototypeOf(value) as unknown;
  return proto === Object.prototype || proto === null;
}

function stripConfidenceValue(value: unknown): unknown {
  if (typeof value === 'string') return stripConfidenceComments(value);
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const stripped = stripConfidenceValue(item);
      if (stripped !== item) changed = true;
      return stripped;
    });
    return changed ? next : value;
  }
  if (value !== null && typeof value === 'object' && isPlainRecord(value)) {
    let changed = false;
    const source = value as Record<string, unknown>;
    const next: Record<string, unknown> = {};
    for (const key of Object.keys(source)) {
      const current = source[key];
      const stripped = stripConfidenceValue(current);
      if (stripped !== current) changed = true;
      next[key] = stripped;
    }
    return changed ? next : value;
  }
  return value;
}

export function stripConfidenceCommentsDeep<T>(value: T): T {
  return stripConfidenceValue(value) as T;
}
